import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { Button, Col, Row } from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { branch, compose, lifecycle, renderComponent, withProps, withStateHandlers } from "recompose";
import { bindActionCreators } from 'redux';
import { cpdPointStatus, defaultTake, routes, userRoles } from '../../../../constants';
import { fullName, getPriorityRole, isBusinessAdmin, isExternalUser, isSuperAdmin, isSystemAdmin } from '../../../../services';
import { adminActions } from '../../../../state/modules/admin';
import { industriesSelector } from '../../../../state/modules/industry/selectors';
import { noCapMap } from '../../../../utils';
import { ConfirmationModal, LoadingButton, SearchForm, SelectFormField, ServerError } from '../../../components';
import CalculatingLoading from '../../calculating-loading';
import { DataTable } from './data-table';
import DownloadActivityButton from './partial/download-activity-button';
import { UserSnapshotTable } from './partial/user-snapshot-table';
import CopyUserCpdActivityModal from './partial/copy-user-cpd-activity-modal';
import { currentUserSelector } from '../../../../state/modules/user';

const individualUserColumns = [
  {
    columnName: 'firstName',
    display: () => 'First name'
  },
  {
    columnName: 'lastName',
    display: () => 'Last name'
  },
  {
    columnName: 'email',
    display: () => 'Email'
  },
  {
    columnName: 'role',
    display: () => 'Role'
  },
  {
    columnName: 'subscriptionStatus',
    display: () => 'Subscription status'
  },
  {
    columnName: 'userStatus',
    display: () => 'User status'
  },
  {
    columnName: 'options',
    display: () => 'Options'
  }
]

const getDownloadReportButton = (label, userId, { yearsFromNow }) => (
  <LinkContainer to={routes.adminPortal.userReport(userId, yearsFromNow)} >
    <Button bsStyle='plain'>
      {label}
    </Button>
  </LinkContainer>
)

const mapStateToProps = (state) => ({
  adminToken: state.session.idToken,
  isDisassociatingUserRole: state.admin.isDisassociatingUserRole,
  isAssigningUserRole: state.admin.isAssigningUserRole,
  currentUser: currentUserSelector(state),
  industries: industriesSelector(state),
  isFetchingUser: state.admin.isFetchingUser,
  userData: state.admin.userData,
  isRemovingUserQuiz: state.admin.isRemovingUserQuiz,
  isTakingSnapshot: state.admin.isTakingSnapshot,
  isDeactivatingUser: state.admin.isDeactivatingUser,
  isDeletingUser: state.admin.isDeletingUser,
  isCopyingActivity: state.admin.isCopyingActivity,
  error: state.admin.error,
})
const mapDispatchToProps = (dispatch) => ({
  getUsers: bindActionCreators(adminActions.admin.getUsers, dispatch),
  snapshotUserTrainingPlan: bindActionCreators(adminActions.admin.snapshotUserTrainingPlan, dispatch),
  changeUserIndustry: bindActionCreators(adminActions.admin.changeUserIndustry, dispatch),
  deleteUser: bindActionCreators(adminActions.admin.deleteUser, dispatch),
  deactivateUser: bindActionCreators(adminActions.admin.deactivateUser, dispatch),
  removeFailedUserQuiz: bindActionCreators(adminActions.admin.removeFailedUserQuiz, dispatch),
  assignUserRole: bindActionCreators(adminActions.admin.assignUserRole, dispatch),
  disassociateUserRole: bindActionCreators(adminActions.admin.disassociateUserRole, dispatch)
})

export const IndividualUserTable = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    ({ industries, selectedIndustry }) => ({
      fetchUsersQuery: {
        skip: 0,
        take: defaultTake,
        tags: '',
        industryIds: fp.map('id')(industries).join(',')
      },
      searchDebounce: undefined,
    }),
    {
      setSelectedIndustry: ({ fetchUsersQuery }) => (industryIds) => ({
        fetchUsersQuery: {
          ...fetchUsersQuery,
          industryIds
        }
      }),
      updateSearchDebounce: () => (searchDebounce) => ({ searchDebounce }),
      onSearchUsers: ({ fetchUsersQuery }) => (e) => ({
        fetchUsersQuery: {
          ...fetchUsersQuery,
          skip: 0,
          tags: e.target.value
        }
      }),
      onLoadMoreUsers: ({ fetchUsersQuery }) => () => ({
        fetchUsersQuery: {
          ...fetchUsersQuery,
          skip: fetchUsersQuery.skip + defaultTake
        }
      }),
      setUpdatingUserRole: () => (updatingUserRole) => ({
        updatingUserRole
      })
    }
  ),
  lifecycle({
    componentDidMount() {
      const {
        fetchUsersQuery,
        getUsers
      } = this.props
      getUsers(fetchUsersQuery)
    },
    componentDidUpdate(prevProps) {
      const {
        fetchUsersQuery,
        getUsers,
        searchDebounce,
        updateSearchDebounce,
        isAssigningUserRole,
        isDisassociatingUserRole,
        setUpdatingUserRole,
        isFetchingUser,
        isCopyingActivity,
        error,
      } = this.props;

      const userRoleUpdated = (prevProps.isAssigningUserRole && !isAssigningUserRole) || (prevProps.isDisassociatingUserRole && !isDisassociatingUserRole);

      // When we start updating user role, update the updating status.
      if (isAssigningUserRole || isDisassociatingUserRole) {
        setUpdatingUserRole(true);
      }

      // Re-fetch the user's detail after the role has been updated.
      // So that the user can see feedback on screen.
      if (userRoleUpdated) {
        getUsers(fetchUsersQuery);
      }

      // Re-fetch the user's detail after the cpd activity was copied.
      if (!isCopyingActivity && prevProps.isCopyingActivity && error === null) {
        getUsers({
          ...fetchUsersQuery,
          take: fetchUsersQuery.skip + fetchUsersQuery.take,
          skip: 0,
        });
      }

      // Set not updating user role after the users has been fetched.
      // Because a user role update will always follow a users fetch here.
      if (!isFetchingUser && prevProps.isFetchingUser) {
        setUpdatingUserRole(false);
      }

      if (!fp.isEqual(prevProps.fetchUsersQuery, fetchUsersQuery)) {
        const debounced = fp.debounce(500)(
          () => {
            getUsers(fetchUsersQuery)
          }
        );
        updateSearchDebounce(debounced);
        searchDebounce && searchDebounce.cancel();
        debounced();
      }

    }
  }),
  branch(
    ({ userData }) => !userData,
    renderComponent(CalculatingLoading)
  ),
  withProps(
    ({ userData }) => ({
      hasMore: userData.result.length < userData.hits
    })
  ),
  withStateHandlers(
    ({
      userData
    }) => ({
      userIndustryEdit: fp.map(
        (user) => ({
          ...user,
          isEditingIndustry: false,
          showConfirmation: false,
          nextIndustry: fp.get('industry.id')(fp.first(user.trainingPlans))
        })
      )(userData.result),
      showUserDetailModal: null,
      showUserDeactivationModal: null,
      showUserDeletionModal: null,
      showCopyCpdActivityModal: null,
    }),
    {
      toggleDetailsModal: ({ showUserDetailModal }) => (userId) => ({
        showUserDetailModal: showUserDetailModal === userId ? null : userId
      }),
      toggleDeactivationModal: ({ showUserDeactivationModal }) => (userId) => ({
        showUserDeactivationModal: showUserDeactivationModal === userId ? null : userId
      }),
      toggleDeletionModal: ({ showUserDeletionModal }) => (userId) => ({
        showUserDeletionModal: showUserDeletionModal === userId ? null : userId
      }),
      toggleCopyCpdActivityModal: ({ showCopyCpdActivityModal }) => (userId) => ({
        showCopyCpdActivityModal: showCopyCpdActivityModal === userId ? null : userId
      }),
      resetData: (state, { userData }) => () => ({
        userIndustryEdit: fp.map(
          (user) => ({
            ...user,
            isEditingIndustry: false,
            showConfirmation: false,
            nextIndustry: fp.get('industry.id')(fp.first(user.trainingPlans))
          })
        )(userData.result)
      }),
      toggleConfirmation: ({ userIndustryEdit }) => (id) => ({
        userIndustryEdit: noCapMap(
          (obj, index) => id === index ? {
            ...obj,
            showConfirmation: !obj.showConfirmation,
          } : obj
        )(userIndustryEdit)
      }),
      toggleIndustryEdit: ({ userIndustryEdit }) => (id) => ({
        userIndustryEdit: noCapMap(
          (obj, index) => id === index ? {
            ...obj,
            isEditingIndustry: !obj.isEditingIndustry,
            nextIndustry: fp.get('industry.id')(fp.first(obj.trainingPlans))
          } : obj
        )(userIndustryEdit)
      }),
      updateNextIndustry: ({ userIndustryEdit }) => (id, nextIndustry) => ({
        userIndustryEdit: noCapMap(
          (obj, index) => id === index ? { ...obj, nextIndustry } : obj
        )(userIndustryEdit)
      })
    }
  ),
  lifecycle({
    componentDidUpdate(prevProps) {
      const {
        isFetchingUser,
        resetData,
        userData
      } = this.props
      if (
        (isFetchingUser && !prevProps.isFetchingUser) ||
        (!fp.equals(userData)(prevProps.userData))
      ) {
        resetData()
      }
    }
  }),
  withProps(
    ({
      userIndustryEdit,
      addTasks,
      requestAccess,
      adminGrants,
      authenticate,
      adminToken,
      toggleIndustryEdit,
      updateNextIndustry,
      industries,
      changeUserIndustry,
      toggleConfirmation,
      removeFailedUserQuiz,
      isRemovingUserQuiz,
      toggleDetailsModal,
      showUserDetailModal,
      snapshotUserTrainingPlan,
      isTakingSnapshot,
      deactivateUser,
      isDeactivatingUser,
      showUserDeactivationModal,
      toggleDeactivationModal,
      deleteUser,
      isDeletingUser,
      showUserDeletionModal,
      toggleDeletionModal,
      assignUserRole,
      disassociateUserRole,
      isDisassociatingUserRole,
      isAssigningUserRole,
      currentUser,
      isFetchingUser,
      updatingUserRole,
      isCopyingActivity,
      showCopyCpdActivityModal,
      toggleCopyCpdActivityModal,
      error,
    }) => ({
      className: 'individual-user-table',
      rows: noCapMap(
        ({ user, roles, trainingPlans, snapshots, subscription, cpdPoints, isEditingIndustry, nextIndustry, showConfirmation }, id) => {
          const failedCpdPoints = fp.flow(
            fp.filter(
              ({ status }) => status === cpdPointStatus.denied
            ),
            fp.get('length')
          )(cpdPoints);

          const userRole = { roleAssignments: roles }
          const isExternal = isExternalUser(userRole);
          const isNonBusinessAdminActiveUser = !isBusinessAdmin(userRole) && user.activated
          const isCurrentUserSuperAdmin = isSuperAdmin(currentUser);
          const isUserSystemAdmin = isSystemAdmin(userRole);
          const isUserSuperAdmin = isSuperAdmin(userRole);

          const priorityRole = getPriorityRole(roles);
          const priorityRoleName = priorityRole === undefined ? "" : priorityRole.name;

          return ({
            ...user,
            role: priorityRoleName,
            userStatus: user.activated ? 'Active' : 'Inactive',
            subscriptionStatus: subscription ? subscription.status : "no subscription",
            options: <div>
              <Button onClick={() => toggleDetailsModal(user.id)}>Details</Button>
              <ConfirmationModal
                className="update-user-details-modal"
                show={showUserDetailModal === user.id}
                actions={[]}
                cancel={() => toggleDetailsModal(user.id)}
                cancelLabel='Back'
                message={
                  <form className='top-gap'>
                    <Row>
                      <Col xs={12}>
                        <ServerError error={error} hideSupport />
                      </Col>
                    </Row>
                    <div className='top-gap user-role'>
                      <span className='title'>User Role:</span>
                      <span>{fp.map('role')(roles).join('/')}</span>
                    </div>
                    {
                      // If the current logged in user is a Super Admin and the user details in the modal is not the super admin.
                      isCurrentUserSuperAdmin && !isUserSuperAdmin ? (
                        <div className='top-gap'>
                          <LoadingButton 
                            className={`top-gap-xs ${isUserSystemAdmin ? 'btn-delete' : ''}`}
                            disabled={updatingUserRole}
                            loading={updatingUserRole}
                            onClick={() => 
                              isUserSystemAdmin ?
                                disassociateUserRole(user.id, userRoles.SystemAdmin) : 
                                assignUserRole(user.id, userRoles.SystemAdmin)
                            }
                            text={isUserSystemAdmin ? "Remove System Admin Role" : "Assign System Admin Role"}
                          />
                        </div>
                      ) : <div></div>
                    }
                    <div className='top-gap flexbox align-center industry'>
                      <span className='title'>Industry:</span>
                      <SelectFormField
                        id=''
                        emptyOption={false}
                        value={nextIndustry}
                        onChange={(e) => updateNextIndustry(id, parseInt(e.target.value, 10))}
                        selectStyle={{ width: '250px'}}
                      >
                        {
                          fp.map(({ id, name }) =>
                            <option key={id} value={id}>{name}</option>
                          )(industries)
                        }
                      </SelectFormField>
                      <Button disabled={fp.flow(
                        fp.map('industry.id'),
                        fp.contains(nextIndustry)
                      )(trainingPlans)} onClick={() => {
                        changeUserIndustry(user.id, nextIndustry)
                      }}>Update</Button>
                    </div>
                    <div className='top-gap flexbox align-center reports'>
                      <span className='title'>Reports:</span>
                      <span>
                        {
                          getDownloadReportButton('Current Period', user.id, { yearsFromNow: 0 })
                        }
                      </span>
                      <span>
                        {
                          getDownloadReportButton('Last Period', user.id, { yearsFromNow: -1 })
                        }
                      </span>
                      <span>
                        <DownloadActivityButton {...{
                          user,
                          trainingPlans,
                          cpdPoints
                        }} />
                      </span>
                    </div>
                    <div className='top-gap flexbox nowrap'>
                      <span className='title'>Actions:</span>
                      <div className='flexbox'>
                        <LoadingButton className='right-gap' text={`Remove ${failedCpdPoints} Failed Quiz`} loading={isRemovingUserQuiz} onClick={() => removeFailedUserQuiz(user.id)} />
                        <LoadingButton text={`Snapshot Training Plan`} loading={isTakingSnapshot} onClick={() => snapshotUserTrainingPlan(user.id)} />
                        {
                          isExternal ? 
                            <LoadingButton
                              className='top-gap-xs right-gap'
                              text={`Transfer user activities`}
                              loading={isCopyingActivity}
                              onClick={() => toggleCopyCpdActivityModal(user.id)}
                            /> :
                            null
                        }
                        {
                          isNonBusinessAdminActiveUser ?
                            <LoadingButton
                              className='top-gap-xs btn-delete'
                              text={`Deactivate`}
                              loading={isDeactivatingUser}
                              onClick={() => toggleDeactivationModal(user.id)}
                            /> :
                            null
                        }
                        {
                          !user.activated ?
                            <LoadingButton
                              className='top-gap-xs btn-delete'
                              text={`Delete`}
                              loading={isDeletingUser}
                              onClick={() => toggleDeletionModal(user.id)}
                            /> :
                            null
                        }
                      </div>
                    </div>

                    <div className='top-gap training-plan'>
                      <span className='title'>Training Plan:</span>
                      <UserSnapshotTable user={user} snapshots={snapshots} />
                    </div>
                  </form>
                }
                headingText={
                  <div className='flexbox justify-between align-center'>
                    <span>{fullName(user)} Details</span>
                    <Button onClick={() => authenticate(adminToken, user.id)}>Login</Button>
                  </div>
                }
              />
              <ConfirmationModal
                className="user-deactivation-modal"
                show={showUserDeactivationModal === user.id}
                cancel={() => toggleDeactivationModal(user.id)}
                actions={[
                  {
                    onConfirmAction: () => {
                      deactivateUser(user.id);
                      toggleDeactivationModal(user.id);
                    },
                    actionLabel: 'Yes, deactivate',
                  }
                ]}
                cancelLabel='Cancel'
                headingText={<div>Deactivate {fullName(user)}</div>}
                message={
                  <div>
                    <p>This is an { isExternal ? 'External' : 'Caddie'} individual user.</p>
                    <p>Deactivate this user will:</p>
                    <ul style={{ textAlign: 'left' }}>
                      <li>Set the user's status to inactive</li>
                      <li>Cancel the user's subscription (if have)</li>
                    </ul>
                    <p>Are you sure you want to deactivate this user?</p>
                  </div>
                }
              />
              <ConfirmationModal
                className="user-deletion-modal"
                show={showUserDeletionModal === user.id}
                cancel={() => toggleDeletionModal(user.id)}
                actions={[
                  {                    
                    onConfirmAction: () => {
                      deleteUser(user.id);
                      toggleDeletionModal(user.id);
                      toggleDetailsModal(user.id);
                    },
                    actionLabel: 'Yes, delete',
                  }
                ]}
                cancelLabel='Cancel'
                headingText={<div>Delete {fullName(user)}</div>}
                message={
                  <div>
                    <p>Are you sure you want to delete this user?</p>
                  </div>
                }
              />
              <CopyUserCpdActivityModal
                showCopyCpdActivityModal={showCopyCpdActivityModal}
                toggleCopyCpdActivityModal={toggleCopyCpdActivityModal}
                user={user}
              />
            </div>
          })
        }
      )(userIndustryEdit),
      columns: individualUserColumns
    })
  )
)(
  ({ onSearchUsers, fetchUsersQuery, isFetchingUser, hasMore, onLoadMoreUsers, setSelectedIndustry, industries, ...props }) => {
    return <div>
      <Row className='no-gutter top-gap'>
        <Col md={3} sm={12}>
          <SearchForm
            label="Search users"
            searchQuery={fetchUsersQuery.tags}
            onSearchChange={onSearchUsers}
          />
        </Col>
        <Col md={3} sm={12} mdOffset={1}>
          <form>
            <SelectFormField
              emptyOption={true}
              label='Select industry'
              onChange={(e) => {
                const industryId = parseInt(e.target.value, 10)
                if (!industryId) {
                  const allIndustryIds = fp.map('id')(industries).join(',')
                  setSelectedIndustry(allIndustryIds)
                } else {
                  setSelectedIndustry(industryId)
                }
              }}>
              {
                fp.map(
                  ({ id, name }) => <option key={id} value={id}>{he.decode(name)}</option>
                )(industries)
              }
            </SelectFormField>
          </form>
        </Col>
      </Row>
      <DataTable {...{...props, isLoading: isFetchingUser}} />
      {
        hasMore ?
          <LoadingButton text='Load More' loading={isFetchingUser} onClick={onLoadMoreUsers} /> :
          null
      }
    </div>
  }
)


// WEBPACK FOOTER //
// ./src/containers/pages/admin-portal/user-portal/individual-user-table.js